import type {
  RequestConfig,
  ResponseData,
  HttpOptions,
  HttpInstance,
  RequestCancel,
  RequestConcurrent,
  UploadFileType
} from './http.types'
import appConfig, { getConfig } from '../../config'
import wx from 'weixin-js-sdk'

// 声明 getCurrentPages 函数
declare function getCurrentPages(): any[]

class Http implements HttpInstance {
  private options: HttpOptions
  private requestCancel: RequestCancel
  private requestConcurrent: RequestConcurrent
  private requestTasks: Map<string, UniApp.RequestTask> = new Map()

  constructor(options: HttpOptions) {
    this.options = Object.assign(
      {
        baseURL: '',
        timeout: 10000,
        withCredentials: false,
        maxConcurrent: 10 // 默认最大并发请求数为10
      },
      options
    )

    // 初始化请求取消集合
    this.requestCancel = {
      cancelTokens: new Map(),
      addToken: (token: string, cancel: () => void) => {
        this.requestCancel.cancelTokens.set(token, cancel)
      },
      removeToken: (token: string) => {
        this.requestCancel.cancelTokens.delete(token)
      },
      cancelRequest: (token: string) => {
        const cancelFn = this.requestCancel.cancelTokens.get(token)
        if (cancelFn) {
          cancelFn()
          this.requestCancel.removeToken(token)
        }
      },
      cancelAllRequests: () => {
        this.requestCancel.cancelTokens.forEach((cancelFn) => {
          cancelFn()
        })
        this.requestCancel.cancelTokens.clear()
      }
    }

    // 初始化请求并发控制
    this.requestConcurrent = {
      queue: [],
      running: 0,
      maxConcurrent: this.options.maxConcurrent || 10,
      pushQueue: (request) => {
        this.requestConcurrent.queue.push(request)
        this.requestConcurrent.runQueue()
      },
      runQueue: () => {
        if (
          this.requestConcurrent.queue.length === 0 ||
          this.requestConcurrent.running >= this.requestConcurrent.maxConcurrent
        ) {
          return
        }

        // 执行队列中的请求
        this.requestConcurrent.running++
        const request = this.requestConcurrent.queue.shift()
        if (request) {
          request()
            .finally(() => {
              this.requestConcurrent.running--
              this.requestConcurrent.runQueue()
            })
            .catch((err) => {
              console.error('Request error:', err)
            })
        }
      }
    }
  }

  /**
   * 处理请求URL
   * @param url 请求路径
   * @returns 完整URL
   */
  private getFullUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return this.options.baseURL + (url.startsWith('/') ? url : `/${url}`)
  }

  /**
   * 发送请求
   * @param config 请求配置
   * @returns Promise
   */
  request<T = any>(config: RequestConfig): Promise<ResponseData<T>> {
    // 创建请求函数
    const requestFn = () => {
      return new Promise<ResponseData<T>>((resolve, reject) => {
        // 处理请求配置
        const fullUrl = this.getFullUrl(config.url || '')

        // 显示loading
        if (config.showLoading) {
          uni.showLoading({
            title: config.loadingText || '加载中...',
            mask: true
          })
        }

        // 处理头部信息
        const header = Object.assign({}, this.options.headers || {}, config.headers || {})

        header.tenantId = 10001

        // 添加token
        const token = uni.getStorageSync(appConfig.tokenKey)
        if (token) {
          header.token = `${token}`
        }

        // 添加租户id
        if (appConfig.staff_id) {
          header.StaffId = appConfig.staff_id
        }

        // 过滤掉值为undefined的参数
        const data = Object.fromEntries(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          Object.entries(config.params || config.data || {}).filter(([_, value]) => value !== undefined)
        )

        // 发起请求
        const requestTask = uni.request({
          url: fullUrl,
          data,
          method: (config.method as UniApp.RequestOptions['method']) || 'GET',
          header,
          timeout: config.timeout || this.options.timeout,
          dataType: 'json',
          responseType: config.responseType as 'text' | 'arraybuffer',
          withCredentials: config.withCredentials !== undefined ? config.withCredentials : this.options.withCredentials,
          success: (res: UniApp.RequestSuccessCallbackResult) => {
            // 隐藏loading
            if (config.showLoading) {
              uni.hideLoading()
            }

            // 移除requestTask
            if (config.cancelTokenKey) {
              this.requestTasks.delete(config.cancelTokenKey)
              this.requestCancel.removeToken(config.cancelTokenKey)
            }

            // 解析响应数据
            const response = res.data as any

            // 判断响应数据格式：通过 errcode 或 code 字段区分
            const isLegacyFormat = 'errcode' in response
            const isNewFormat = 'code' in response

            // 处理文件流类型
            if (config.responseType === 'arraybuffer') {
              resolve(response)
              return
            }

            let isSuccess = false
            let errorCode: string | number = ''
            let errorMessage = ''

            if (isLegacyFormat) {
              // 原有格式：errcode/errmsg
              isSuccess = response.errcode === 0
              errorCode = response.errcode
              errorMessage = response.errmsg || '请求失败'
            } else if (isNewFormat) {
              // 新格式：code/message/msg (兼容 request.js)
              const { code, message, msg, status } = response
              isSuccess = code === '000000' || status === 200
              errorCode = code
              errorMessage = message || msg || '发生错误，请稍后重试'

              // 标准化响应数据
              response.message = message || msg || 'SUCCESS'
            } else {
              // 默认按成功处理
              isSuccess = true
            }

            // 响应错误处理
            if (!isSuccess) {
              // 根据错误码处理不同的错误
              if (isLegacyFormat) {
                // 原有格式错误处理
                switch (response.errcode) {
                  // 未授权
                  case 401:
                  case -10000:
                    // 清除token
                    uni.removeStorageSync(appConfig.tokenKey)
                    // 跳转到登录页
                    uni.redirectTo({ url: '/pages/login/index' })
                    break
                  case 403: // 禁止访问
                    uni.showToast({
                      title: '无权限访问',
                      icon: 'none',
                      duration: 2000
                    })
                    break
                  default:
                    // 显示错误信息
                    if (!config.showErrorToast) {
                      uni.showToast({
                        title: errorMessage,
                        icon: 'none',
                        duration: 2000
                      })
                    } else {
                      reject(errorMessage)
                      return
                    }
                }
              } else if (isNewFormat) {
                // 新格式错误处理 (兼容 request.js)
                const message_ = response.message || response.msg || ''
                
                // 登录失效处理
                if (
                  (response.code === '100001' && message_.indexOf('token不存在') > -1) ||
                  message_.indexOf('登录') > -1 || response.code === '100004' || response.code === '100003'
                ) {
                  // H5环境下返回到小程序
                  // #ifdef H5
                  // 清除所有缓存数据
                  uni.clearStorageSync()
                  uni.showToast({
                      title: message_,
                      icon: 'none',
                      duration: 1000
                    })
                    setTimeout(() =>{
                      wx.miniProgram.reLaunch({
                        url: '/pages/login/index?type=clear'
                      })
                    }, 1000)
                  
                  // #endif
                } else {
                  // 其他错误
                  if (!config.showErrorToast) {
                    uni.showToast({
                      title: errorMessage,
                      icon: 'none',
                      duration: 2000
                    })
                  } else {
                    reject(errorMessage)
                    return
                  }
                }
              }

              // 根据格式分别处理错误对象
              if (isLegacyFormat) {
                // 原有格式：直接抛出 Error
                reject(new Error(errorMessage))
              } else if (isNewFormat) {
                // 新格式：返回包含 code, data, errMsg 的对象 (兼容 request.js)
                reject({
                  code: errorCode,
                  data: response.data || {},
                  errMsg: errorMessage
                })
              }
              return
            }

            resolve(response)
          },
          fail: (err: UniApp.GeneralCallbackResult) => {
            // 隐藏loading
            if (config.showLoading) {
              uni.hideLoading()
            }

            // 移除requestTask
            if (config.cancelTokenKey) {
              this.requestTasks.delete(config.cancelTokenKey)
              this.requestCancel.removeToken(config.cancelTokenKey)
            }

            // 请求重试
            if (config.retry && config.retryTimes && config.retryTimes > 0) {
              // 设置重试次数和延迟
              const retryCount = config.retryTimes
              const retryDelay = config.retryDelay || 1000

              // 记录重试次数
              config.retryTimes--

              // 创建新的Promise进行延迟重试
              const retryRequest = new Promise<ResponseData<T>>((retryResolve) => {
                setTimeout(() => {
                  console.log(`Retrying request (${retryCount - (config.retryTimes ?? 0)}/${retryCount})`, config.url)
                  retryResolve(this.request(config))
                }, retryDelay)
              })

              retryRequest.then(resolve).catch(reject)
              return
            }

            // 错误处理
            if (err.errMsg.includes('timeout')) {
              uni.showToast({
                title: '请求超时，请检查网络',
                icon: 'none',
                duration: 2000
              })
            } else if (err.errMsg.includes('abort')) {
              console.log('请求已取消')
            } else {
              uni.showToast({
                title: err.errMsg || '请求失败',
                icon: 'none',
                duration: 2000
              })
            }

            reject(err)
          },
          complete: () => {
            // 自定义完成回调可以在这里添加
          }
        })

        // 保存请求任务，用于取消
        if (config.cancelTokenKey) {
          this.requestTasks.set(config.cancelTokenKey, requestTask)
          // 设置取消函数
          this.requestCancel.addToken(config.cancelTokenKey, () => {
            requestTask.abort()
          })
        }
      })
    }

    // 如果设置了maxConcurrent，则使用请求并发控制
    if (this.options.maxConcurrent) {
      return new Promise<ResponseData<T>>((resolve, reject) => {
        this.requestConcurrent.pushQueue(() => {
          return requestFn().then(resolve).catch(reject)
        })
      })
    }

    // 直接发送请求
    return requestFn()
  }

  /**
   * GET请求
   * @param url 请求地址
   * @param params 请求参数
   * @param config 请求配置
   * @returns Promise
   */
  get<T = any>(url: string, params?: Record<string, any>, config?: RequestConfig): Promise<ResponseData<T>> {
    return this.request<T>({
      method: 'GET',
      url,
      params,
      ...config
    })
  }

  /**
   * POST请求
   * @param url 请求地址
   * @param data 请求数据
   * @param config 请求配置
   * @returns Promise
   */
  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ResponseData<T>> {
    return this.request<T>({
      method: 'POST',
      url,
      data,
      ...config
    })
  }

  /**
   * PUT请求
   * @param url 请求地址
   * @param data 请求数据
   * @param config 请求配置
   * @returns Promise
   */
  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ResponseData<T>> {
    return this.request<T>({
      method: 'PUT',
      url,
      data,
      ...config
    })
  }

  /**
   * DELETE请求
   * @param url 请求地址
   * @param params 请求参数
   * @param config 请求配置
   * @returns Promise
   */
  delete<T = any>(url: string, params?: Record<string, any>, config?: RequestConfig): Promise<ResponseData<T>> {
    return this.request<T>({
      method: 'DELETE',
      url,
      params,
      ...config
    })
  }

  /**
   * 取消请求
   * @param cancelTokenKey 取消标识
   */
  cancelRequest(cancelTokenKey: string): void {
    this.requestCancel.cancelRequest(cancelTokenKey)
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests(): void {
    this.requestCancel.cancelAllRequests()
  }

  /**
   * 上传文件
   * @param filePath 文件路径
   * @param fileType 文件类型
   * @param options 上传选项
   * @returns Promise
   */
  uploadFile<T = any>(
    filePath: string,
    fileType: UploadFileType,
    options: {
      name?: string
      formData?: Record<string, any>
      showLoading?: boolean
      loadingText?: string
    } = {}
  ): Promise<ResponseData<T>> {
    return new Promise((resolve, reject) => {
      // 显示loading
      if (options.showLoading) {
        uni.showLoading({
          title: options.loadingText || '上传中...',
          mask: true
        })
      }

      // 根据文件类型选择上传地址
      let uploadUrl = ''
      switch (fileType) {
        case 'image':
          uploadUrl = appConfig.uploadApi
          break
        case 'video':
          uploadUrl = appConfig.uploadApi //ppConfig.uploadVideoApi
          break
        default:
          uploadUrl = appConfig.uploadApi
      }

      // 处理头部信息
      const header = Object.assign({}, this.options.headers || {})
      const formData = Object.assign({ directory: getConfig('uploadDir') }, options.formData || {})
      const token = uni.getStorageSync(appConfig.tokenKey)
      if (token) {
        // header.Authorization = `${token}`
        header.token = `${token}`
      }

      // 发起上传请求
      uni.uploadFile({
        url: uploadUrl,
        filePath,
        name: options.name || 'file',
        header,
        formData,
        success: (res) => {
          // 隐藏loading
          if (options.showLoading) {
            uni.hideLoading()
          }

          try {
            const response = JSON.parse(res.data) as ResponseData<T>
            resolve(response)
          } catch (error) {
            console.error('解析响应数据失败', error)
            reject(new Error('解析响应数据失败'))
          }
        },
        fail: (err) => {
          // 隐藏loading
          if (options.showLoading) {
            uni.hideLoading()
          }

          // 错误处理
          if (err.errMsg.includes('timeout')) {
            uni.showToast({
              title: '上传超时，请检查网络',
              icon: 'none',
              duration: 2000
            })
          } else {
            uni.showToast({
              title: err.errMsg || '上传失败',
              icon: 'none',
              duration: 2000
            })
          }

          reject(err)
        }
      })
    })
  }
}

// 创建HTTP实例的工厂函数
export const createHttp = (options: HttpOptions): HttpInstance => {
  return new Http(options)
}

// 默认导出，使用默认配置创建HTTP实例
export default createHttp({
  baseURL: appConfig.baseApi,
  timeout: appConfig.timeout,
  withCredentials: false,
  maxConcurrent: appConfig.maxConcurrent
})
